import { Link } from 'react-router-dom';
import { Card, CardContent } from '@components/ui/Card';
import SEO from '@components/SEO';
import { Users, BookOpen, Newspaper, Heart, ArrowRight } from 'lucide-react';
import Button from '@components/ui/Button';

const subPages = [
  {
    title: 'Über uns',
    description: 'Lernen Sie das Team des Aktionsbündnisses kennen',
    icon: Users,
    link: '/ueber-uns',
    color: 'blue',
  },
  {
    title: 'LeseZeichen',
    description: 'Literaturinitiative des Aktionsbündnisses',
    icon: BookOpen,
    link: '/lesezeichen',
    color: 'purple',
  },
  {
    title: 'Literatur',
    description: 'Bücher zur Geschichte der Sternwarte',
    icon: Newspaper,
    link: '/literatur',
    color: 'green',
  },
  {
    title: 'Presse + Medien',
    description: 'Berichte und Artikel über die Sternwarte',
    icon: Newspaper,
    link: '/presse',
    color: 'amber',
  },
  {
    title: 'Unterstützen',
    description: 'Helfen Sie mit, die Sternwarte zu erhalten',
    icon: Heart,
    link: '/unterstuetzen',
    color: 'red',
  },
];

export default function AktionsbuendnisPage() {
  return (
    <div className="py-12 bg-slate-950 min-h-screen">
      <SEO
        title="Aktionsbündnis"
        pathname="/aktionsbuendnis"
        description="Das Aktionsbündnis Alte Sternwarte - Engagiert für den Erhalt und die Nutzung der historischen Sternwarte Mannheim."
        keywords={['Aktionsbündnis', 'Verein', 'Engagement', 'Ehrenamt', 'Denkmalschutz']}
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">Aktionsbündnis</h1>
          <p className="text-lg text-slate-400 max-w-2xl">
            Das Aktionsbündnis ist ein Zusammenschluss von Institutionen, Vereinen und
            Privatpersonen aus Mannheim, denen der Erhalt der Alten Sternwarte Mannheim am
            Herzen liegt.
          </p>
        </div>

        {/* History */}
        <Card className="mb-10">
          <CardContent className="p-6">
            <h2 className="text-xl font-bold text-white mb-4">Geschichte</h2>
            <p className="text-slate-300 leading-relaxed">
              Das Aktionsbündnis entstand im Juli 2010 mit ca. 20 Personen. Seit 2016 gehört das
              Aktionsbündnis als eigenständiges Projekt zum Verein Stadtbild Mannheim e.V.
            </p>
          </CardContent>
        </Card>

        {/* Sub Pages Grid */}
        <h2 className="text-2xl font-bold text-white mb-6">Mehr erfahren</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {subPages.map((page) => {
            const Icon = page.icon;
            const colorClasses: Record<string, { bg: string; text: string }> = {
              blue: { bg: 'bg-blue-600/10', text: 'text-blue-400' },
              purple: { bg: 'bg-purple-600/10', text: 'text-purple-400' },
              green: { bg: 'bg-green-600/10', text: 'text-green-400' },
              amber: { bg: 'bg-amber-600/10', text: 'text-amber-400' },
              red: { bg: 'bg-red-600/10', text: 'text-red-400' },
            };
            const colors = colorClasses[page.color];

            return (
              <Link key={page.link} to={page.link}>
                <Card hover className="h-full">
                  <CardContent className="p-6">
                    <div
                      className={`w-12 h-12 ${colors.bg} rounded-lg flex items-center justify-center mb-4`}
                    >
                      <Icon className={colors.text} size={24} />
                    </div>
                    <h3 className="text-white font-semibold mb-2">{page.title}</h3>
                    <p className="text-slate-400 text-sm">{page.description}</p>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <Link to="/unterstuetzen">
            <Button size="lg">
              Werden Sie Unterstützer
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
